import React from 'react';
import { Icon } from '@iconify/react';

// Unicode emoji character → fluent-emoji icon name
const EMOJI_MAP: Record<string, string> = {
  // Maritime & Vessels
  '🚢': 'ship', '🛳️': 'passenger-ship', '⛴️': 'ferry', '🚤': 'speedboat',
  '🛥️': 'motor-boat', '⛵': 'sailboat', '🚁': 'helicopter', '✈️': 'airplane',
  '🛩️': 'small-airplane', '🛟': 'ring-buoy',
  // Tools & Maintenance
  '🔧': 'wrench', '🔩': 'nut-and-bolt', '⚙️': 'gear', '🔨': 'hammer',
  '⚒️': 'hammer-and-pick', '🛠️': 'hammer-and-wrench', '⛏️': 'pick',
  '🪛': 'screwdriver', '🧹': 'broom', '🧽': 'sponge', '🪣': 'bucket',
  '🧰': 'toolbox', '⚓': 'anchor', '🪝': 'hook',
  // Documents & Management
  '📝': 'memo', '📋': 'clipboard', '📄': 'page-facing-up', '📃': 'page-with-curl',
  '📊': 'bar-chart', '📈': 'chart-increasing', '📉': 'chart-decreasing',
  '📁': 'file-folder', '📂': 'open-file-folder', '🗂️': 'card-index-dividers',
  '📑': 'bookmark-tabs', '🗒️': 'spiral-notepad', '📅': 'calendar',
  '📆': 'tear-off-calendar', '🗓️': 'spiral-calendar', '📌': 'pushpin',
  '📍': 'round-pushpin', '🔖': 'bookmark', '🏷️': 'label', '📦': 'package',
  '💼': 'briefcase',
  // People & Roles
  '👷': 'construction-worker', '👷‍♂️': 'man-construction-worker',
  '👨‍✈️': 'man-pilot', '👩‍✈️': 'woman-pilot',
  '👤': 'bust-in-silhouette', '👥': 'busts-in-silhouette',
  '🧑': 'person', '👨': 'man', '👩': 'woman',
  // Alerts & Status
  '⚠️': 'warning', '🚨': 'police-car-light', '🔴': 'red-circle',
  '🟢': 'green-circle', '🟡': 'yellow-circle', '🔵': 'blue-circle',
  '✅': 'check-mark-button', '❌': 'cross-mark', '❗': 'red-exclamation-mark',
  '❓': 'red-question-mark', '🚫': 'prohibited', '⛔': 'no-entry',
  '🛑': 'stop-sign', '🔔': 'bell', '📢': 'loudspeaker', '📣': 'megaphone',
  // Navigation & Location
  '🗺️': 'world-map', '🧭': 'compass', '🏴': 'black-flag', '🚩': 'triangular-flag',
  // Nature / Weather (maritime context)
  '🌊': 'water-wave', '💧': 'droplet', '🌬️': 'wind-face', '⛅': 'sun-behind-cloud',
  '☁️': 'cloud', '⛈️': 'cloud-with-lightning-and-rain', '🌧️': 'cloud-with-rain',
  '🌩️': 'cloud-with-lightning',
  // Awards & Grades
  '⭐': 'star', '🌟': 'glowing-star', '🏆': 'trophy', '🏅': 'sports-medal',
  '🎖️': 'military-medal', '🥇': '1st-place-medal', '🥈': '2nd-place-medal',
  '🥉': '3rd-place-medal',
  // Time
  '⏱️': 'stopwatch', '⏰': 'alarm-clock', '⌚': 'watch', '🕐': 'one-oclock',
  // Safety & Health
  '⛑️': 'rescue-workers-helmet', '🦺': 'safety-vest', '🔑': 'key',
  '🔒': 'locked', '🔓': 'unlocked', '🛡️': 'shield',
  '💊': 'pill', '🩺': 'stethoscope', '🩹': 'adhesive-bandage',
  '🚒': 'fire-engine', '🚑': 'ambulance', '🔥': 'fire', '🧯': 'fire-extinguisher',
  // Energy & Power
  '⚡': 'high-voltage', '🔋': 'battery', '💡': 'light-bulb', '🔦': 'flashlight',
  // Monitoring
  '👁': 'eye', '👁️': 'eye', '🔭': 'telescope', '🔬': 'microscope',
  '📡': 'satellite-antenna',
  // Misc work
  '🏗️': 'building-construction', '🏭': 'factory', '🚛': 'articulated-lorry',
  '🚚': 'delivery-truck', '⛽': 'fuel-pump', '🪤': 'mouse-trap',
};

interface Props {
  emoji: string;
  size?: number;
  bgColor?: string;
  borderRadius?: number;
}

export default function FluentEmoji({ emoji, size = 36, bgColor = '#f1f5f9', borderRadius = 8 }: Props) {
  // Strip VS16 variation selector for lookup
  const normalized = emoji.replace(/️/g, '');
  const iconName = EMOJI_MAP[emoji] ?? EMOJI_MAP[normalized];

  return (
    <div style={{
      width: size, height: size, borderRadius, background: bgColor,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      flexShrink: 0,
    }}>
      {iconName
        ? <Icon icon={`fluent-emoji:${iconName}`} width={size * 0.6} height={size * 0.6} />
        : <span style={{ fontSize: size * 0.5, lineHeight: 1 }}>{emoji}</span>
      }
    </div>
  );
}
